import React from "react";
import {Link} from "@heroui/link";
import {
    getPlatformAndContentTypeByUrl,
} from "@/components/DownloaderApp/platform-detection";
import {
    buildUrlWithParams, getKickChannelInfo,
    getKickClip, getKickClips,
    getKickLivestream, getKickPremiumVod,
    getKickVod, getKickVod2, getTwitchLive,
} from "@/components/DownloaderApp/kick";
import {getTwitchClip, getTwitchVod} from "@/components/DownloaderApp/twitch";
import {
    CHROME_EXTENSION_URL,
    fetchText, hlsParse, isDev, isStaging,
    slugify, tryCatch,
} from "@/components/DownloaderApp/utils";
import {ContentType, PlatformContentInfo, Video} from "@/components/DownloaderApp/types";
import {logger} from "@/components/DownloaderApp/logger";
import {showUpsellModal} from "@/components/DownloaderApp/UpsellPopup";
import {isInNewUpsellExperiment} from "@/utils/is-in-new-upsell-experiment";
import {showTwitchUpsellModal} from "@/components/DownloaderApp/TwitchUpsellPopup";
import {showTwitchPremiumNoticeModalThrottled} from "@/components/DownloaderApp/TwitchPremiumNoticePopup";
import {getLicenseKey} from "@/components/DownloaderApp/usePremium";
import {SUPPORT_EMAIL} from "@/utils/email";
import {getRumble} from "@/components/DownloaderApp/rumble";
import {ChromeExtensionPopup} from "@/components/DownloaderApp/ChromeExtensionPopup";
import {getBitchuteVideoUrl} from "@/components/DownloaderApp/bitchute";

interface GetVideoDetailsInput {
    refresh: (l: string | null) => void;
    track: (event: string, data?: Record<string, unknown>) => void;
    extension: unknown;
    url: string;
    isHidden: boolean;
    show: (opts: unknown) => Promise<{ accepted: boolean; data?: unknown }>;
    updateMonitoringContext: (ctx: { platform: string; type: string }) => void;
}

let isTwitchEnabled = isStaging || isDev;
isTwitchEnabled = true;

export async function getVideoDetails({
                                          refresh,
                                          track,
                                          extension,
                                          url,
                                          isHidden,
                                          show,
                                          updateMonitoringContext
                                      }: GetVideoDetailsInput): Promise<Video> {
    const contentType = getPlatformAndContentTypeByUrl(url);

    const {platform, type, id, fromSec, channel} = contentType as PlatformContentInfo;
    url = contentType?.url || url;
    updateMonitoringContext({platform, type});
    let video: Pick<Video, 'platform' | 'type' | 'url' | 'fromSec' | 'isPremium' | 'fetchWithExtension'> = {
        platform, type, url, fromSec, isPremium: false, fetchWithExtension: false
    }

    let finalVideo: Video;

    if (platform === "direct-playlist") {
        const playlistUrl = new URL(url);
        let useExtensionForFetch = playlistUrl.origin === window.location.origin
            && playlistUrl.pathname.startsWith('/api/proxy/api.vodvod.top/m3u8/')
            && playlistUrl.pathname.endsWith('.m3u8');

        if (useExtensionForFetch && !extension) {
            await show({
                component: ChromeExtensionPopup,
                data: {source: 'vodvod'},
            });
            throw {
                status: 69,
                msg: "To download VodVod playlists, install our browser extension, refresh this page, and try again.",
            };
        }

        try {
            let playlistText: string;
            if (useExtensionForFetch) {
                playlistText = await window.sendMessage("fetch-text", url);
            } else {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                playlistText = await response.text();
            }
            // Validate it's actually an m3u8 playlist
            if (!playlistText.includes('#EXTM3U')) {
                throw new Error('Not a valid m3u8 playlist');
            }
        } catch (directFetchError) {
            if (useExtensionForFetch) {
                logger.log("Required extension playlist fetch failed:", directFetchError);
                throw directFetchError;
            }
            logger.log("Direct playlist fetch failed:", directFetchError);

            if (!extension) {
                throw {
                    status: 69,
                    msg: <>This playlist cannot be accessed directly. Try installing <Link
                        href={CHROME_EXTENSION_URL} target="_blank" className="text-blue-700 underline">our
                        browser extension</Link> to bypass restrictions.</>
                };
            }
            // Check if extension can access this playlist
            try {
                const extensionFetchResult = await window.sendMessage("fetch-text", url);
                if (extensionFetchResult && extensionFetchResult.includes('#EXTM3U')) {
                    useExtensionForFetch = true;

                } else {
                    throw new Error('Extension also failed to fetch playlist');
                }
            } catch (extensionError) {
                logger.log("Extension fetch also failed:", extensionError);
                throw {
                    status: 69,
                    msg: <>This playlist cannot be accessed. It may require <b>authentication</b> or
                        is <b>blocked</b>.</>
                };
            }
        }

        finalVideo = {
            ...video,
            videoFileName: 'unnamed-video-' + Date.now(),
            videoPlaylist: url,
            videoName: '',
            videoThumbnail: '',
            slug: null,
            fetchWithExtension: useExtensionForFetch
        }
    } else if (platform === "bitchute" && type === "vod" && id) {
        let downloadLink: string;
        try {
            downloadLink = await getBitchuteVideoUrl(id);
        } catch (error) {
            throw {
                status: 69,
                msg: error instanceof Error
                    ? `Could not retrieve the BitChute video: ${error.message}`
                    : "Could not retrieve the BitChute video. Please try again.",
                cause: error,
            };
        }
        finalVideo = {
            ...video,
            videoFileName: `bitchute-video-${id}`,
            videoName: '',
            videoPlaylist: '',
            videoThumbnail: '',
            slug: null,
            downloadLink,
        }
    } else if (platform === "twitch" && type === "clip") {
        const url2 = id ? `https://clips.twitch.tv/${id}` : url
        const downloadLink = await getTwitchClip(url2);
        finalVideo = {
            ...video,
            videoFileName: `twitch-clip-${id}`,
            videoName: '',
            videoPlaylist: '',
            videoThumbnail: '',
            slug: null,
            downloadLink
        }
    } else if (platform === "kick" && type === "clips") {
        // await getKickClips(id).then(async a => {
        //     // resetForm();
        //     const {accepted, data} = await show({
        //         component: ClipsPopup,
        //         data: {
        //             scrollBehavior: "inside",
        //             className: "h-4/5",
        //             size: '4xl',
        //             clips: a.clips,
        //             channelName: id,
        //         },
        //     })
        // })
        return video as Video; // event though its not a full video
    } else if (platform === "kick" && type === "clip") {
        let [clip, error] = await tryCatch(getKickClip(id));
        if (error) {
            throw error
        }
        logger.log(clip)
        let date;
        try {
            date = clip.started_at.split("T")[0]
        } catch (error) {

        }
        finalVideo = {
            ...video,
            videoFileName: slugify(`${date} ${clip.channel.slug} ${clip.title}`),
            slug: clip.channel.slug,
            videoName: clip.title,
            videoPlaylist: clip.clip_url,
            videoThumbnail: clip.thumbnail_url,
        }

        if (clip.clip_url.endsWith(".mp4")) {
            finalVideo = {
                ...finalVideo,
                downloadLink: clip.clip_url,
            }
        }
    } else if (platform === "twitch" && type === "vod") {
        let twitchVodDetails;
        try {
            twitchVodDetails = await getTwitchVod(id);
            // availableFormats, hqPlaylist, masterPlaylistText, url
        } catch (err) {
            const isSubscriberOnly = err === '403 Forbidden';
            if (!isSubscriberOnly) {
                throw err;
            }
            // Premium (subscriber-only) Twitch content is hidden behind a paywall.
            // Enabled on dev and staging for now; an applied license key bypasses it.
            if (isTwitchEnabled && !getLicenseKey()) {
                track("[Premium] Twitch Subscriber Only VOD paywalled", {vodId: id})
                showTwitchUpsellModal(show, {
                    source: 'twitch-direct-download',
                    vodId: id,
                })
                throw {status: 403, vodId: id, platform: "twitch"};
            }
            // showUpsellModal()
            track("Twitch Subscriber Only VOD requested", {vodId: id})
            console.log({twitchVodDetails})

            const [premiumResult, premiumError] = await tryCatch(fetch(buildUrlWithParams('/api/get-premium-content-2', {
                url2: `https://www.twitch.tv/videos/${id}`
            }), {
                headers: {
                    'X-License-Key': getLicenseKey() || '',
                },
            }).then(a => a.json()))


            if (premiumError) {
                track("[Premium] Failed to load VOD ", {})
                throw {
                    status: 69,
                    msg: <>Subs-only VOD couldn't be downloaded. Please, reach out to use
                        at <b>{SUPPORT_EMAIL}</b></>
                }
            }

            if (premiumResult.error) {
                track("[Premium] Failed to load VOD ", {message: premiumResult.error})
                throw {
                    status: 69,
                    msg: <>{premiumResult.error}</>,
                    upgradePlatform: premiumResult.code === "LICENSE_PLATFORM_MISMATCH" ? "twitch" : undefined,
                    vodId: id,
                };
            }

            if (!premiumResult.value) {
                track("[Premium] Failed to load VOD ", {message: premiumResult.error})
                throw {
                    status: 69,
                    msg: <>Subs-only VOD couldn't be generated. Please, reach out to use
                        at <b>{SUPPORT_EMAIL}</b></>
                };
            }

            // Inform users that premium Twitch downloads will soon be paid (max once / 48h).
            // await showTwitchPremiumNoticeModalThrottled(show, {source: 'twitch-vod-download'});

            twitchVodDetails = {
                isSubscriberOnly: true,
                url: premiumResult.value
            }
        }

        finalVideo = {
            ...video,
            twitchVodDetails,
            videoFileName: `twitch-vod-${id}`,
            videoName: "",
            videoPlaylist: "",
            videoThumbnail: "",
            fetchWithExtension: true,
            slug: null
        }
        logger.log({twitchVodDetails})
    } else if (platform === "kick" && type === "vod") {
        const result = await getKickVod2(url, channel ? await getKickChannelInfo(channel) : null);

        if (!isHidden && !result.ok) {
            // cut out all errors...

            if (result.status === 429) {
                throw {
                    status: 69,
                    message: "429...",
                    msg: <span>Too many requests from your IP, wait a while to continue later.</span>
                }
            }

            if (result.status !== 403) {
                throw new Error();
            }
        }

        const vod = result.json?.video2;
        let vodSource = vod?.source ?? '';
        let vodChannel = vod?.channel ?? channel ?? '';

        const isPremium = isHidden || result.status === 403 || vod?.status !== "public";

        if (isPremium) {
            // it's 403
            if (!getLicenseKey()) {
                showUpsellModal(show, {
                    source: 'direct-download',
                    vodId: id,
                    vodUrl: url,
                    isVariant1: isInNewUpsellExperiment(),
                })
                throw {
                    status: 403,
                    vodId: id,
                    vodUrl: url
                };
            }
            // 403, and license set...

            track("[Premium] Premium VOD requested", {vod})
            // sub only, license set
            const [premiumResult, premiumError] = await tryCatch(getKickPremiumVod({
                uuid: id,
                url: encodeURIComponent(url),
                channel: vodChannel,
                isDev: isDev ? 'true' : undefined,
                source: 'downloader',
                isHidden
            }));

            console.log({premiumResult, premiumError})

            if (premiumError) {
                track("[Premium] Failed to load VOD ", {vod})
                throw {
                    status: 69,
                    msg: <>Subs-only VOD couldn't be downloaded. Please, reach out to use
                        at <b>{SUPPORT_EMAIL}</b></>
                }
            }

            if (premiumResult.error) {
                track("[Premium] Failed to load VOD ", {message: premiumResult.error})
                throw {
                    status: 69,
                    msg: <>{premiumResult.error}</>,
                    upgradePlatform: premiumResult.code === "LICENSE_PLATFORM_MISMATCH" ? "kick" : undefined,
                    vodId: id,
                    vodUrl: url,
                };
            }
            if (!premiumResult.value && !premiumResult.sources?.length) {
                track("[Premium] Failed to load VOD ", {message: premiumResult.error})
                throw {
                    status: 69,
                    msg: <>Subs-only VOD couldn't be generated. Please, reach out to use
                        at <b>{SUPPORT_EMAIL}</b></>
                };
            }
            if (getLicenseKey()) {
                refresh();
                setTimeout(refresh, 2000);
                setTimeout(refresh, 4000);
            }
            if (!result.json) {
                track("[Premium] Failed to load VOD ", {message: "empty kick api response"})
                throw {
                    status: 69,
                    msg: <>Kick didn't return video details. Please, try again in a while.</>
                };
            }
            try {
                vodSource = premiumResult.sources?.length
                    ? await Promise.any(premiumResult.sources.map(async (source: string) => {
                        const response = await fetch(source);
                        if (!response.ok) {
                            throw new Error(`${source} returned HTTP ${response.status}`);
                        }
                        if (!(await response.text()).startsWith("#EXTM3U")) {
                            throw new Error(`${source} did not return a valid HLS playlist`);
                        }
                        return source;
                    }))
                    : premiumResult.value;
            } catch (error) {
                track("[Premium] Failed to load VOD ", {vod, message: String(error)})
                throw {
                    status: 69,
                    msg: <>Subs-only VOD couldn't be downloaded. Please, reach out to use
                        at <b>{SUPPORT_EMAIL}</b></>
                };
            }
            vodChannel = vod?.channel || premiumResult.channel || vodChannel;
            track("[Premium] Succeeded to load VOD", {vod, source: vodSource, channel: vodChannel})
        }

        logger.log(vod)
        const date = vod?.startTime?.split("T")[0] ?? '';
        const vodTitle = vod?.title ?? '';
        const profilePicture = vod?.profilePicture ?? undefined;
        const vodThumbnail = vod?.thumbnailUrl ?? profilePicture ?? '';
        finalVideo = {
            ...video,
            videoFileName: slugify(`${date} ${vodChannel} ${vodTitle}`),
            videoName: vodTitle,
            videoThumbnail: vodThumbnail,
            profilePicture,
            slug: vodChannel,
            videoPlaylist: vodSource,
            isPremium,
            uuid: id,
        }
    } else if (platform === "kick" && type === "live") {
        // const id = 'eddie';
        const response = await getKickLivestream(id);

        // const [, , , , random1, random2] = response.livestream.thumbnail.url.split("/");
        // const date = new Date(response.livestream.created_at)
        if (!response.livestream?.is_live) {
            throw new Error("Could not load the live playlist because this Kick channel is currently offline.")
        }

        const uuid = response.previous_livestreams.find(({id}) => response.livestream.id === id)?.video?.uuid;
        if (!uuid) {
            throw {
                status: 69,
                msg: <><b>{response.user.username}</b> has just started streaming, but we need <b>one more
                    minute</b> to start downloading it! Try again soon.</>
            };
        }

        const vod = await getKickVod(uuid)

        finalVideo = {
            ...video,
            videoFileName: slugify(`${vod.livestream.channel.slug} ${vod.livestream.session_title}`),
            videoName: vod.livestream.session_title,
            videoThumbnail: vod.livestream.thumbnail || vod.livestream?.channel?.user?.profilepic,
            slug: vod.livestream.channel.slug,
            videoPlaylist: vod.source,
            uuid,
        }
    } else if (platform === "twitch" && type === "live") {
        const response = await getTwitchLive(id);

        const videoId = response.id;
        if (!videoId) {
            throw {
                status: 69,
                msg: <>Couldn't find livestream for <b>@{id}</b>. Livestream is private, or channel doesn't exist</>
            };
        }

        let twitchVodDetails;
        try {
            twitchVodDetails = await getTwitchVod(videoId);
        } catch (err) {
            const isSubscriberOnly = err === '403 Forbidden';
            if (!isSubscriberOnly) {
                throw err;
            }
            // Premium (subscriber-only) Twitch content is hidden behind a paywall.
            // Enabled on dev and staging for now; an applied license key bypasses it.
            if (isTwitchEnabled && !getLicenseKey()) {
                track("[Premium] Twitch Subscriber Only VOD paywalled", {vodId: videoId})
                showTwitchUpsellModal(show, {
                    source: 'twitch-direct-download',
                    vodId: videoId,
                })
                throw {status: 403, vodId: id, platform: "twitch"};
            }
            // showUpsellModal()
            track("Twitch Subscriber Only VOD requested", {videoId})
            console.log({twitchVodDetails})

            const [premiumResult, premiumError] = await tryCatch(fetch(buildUrlWithParams('/api/get-premium-content-2', {
                url2: `https://www.twitch.tv/videos/${videoId}`
            }), {
                headers: {
                    'X-License-Key': getLicenseKey() || '',
                },
            }).then(a => a.json()))


            if (premiumError) {
                track("[Premium] Failed to load VOD ", {videoId})
                throw {
                    status: 69,
                    msg: <>Subs-only VOD couldn't be downloaded. Please, reach out to use
                        at <b>{SUPPORT_EMAIL}</b></>
                }
            }

            if (premiumResult.error) {
                track("[Premium] Failed to load VOD ", {message: premiumResult.error})
                throw {
                    status: 69,
                    msg: <>{premiumResult.error}</>,
                    upgradePlatform: premiumResult.code === "LICENSE_PLATFORM_MISMATCH" ? "twitch" : undefined,
                    vodId: videoId,
                };
            }

            if (!premiumResult.value) {
                track("[Premium] Failed to load VOD ", {message: premiumResult.error})
                throw {
                    status: 69,
                    msg: <>Subs-only VOD couldn't be generated. Please, reach out to use
                        at <b>{SUPPORT_EMAIL}</b></>
                };
            }

            // Inform users that premium Twitch downloads will soon be paid (max once / 48h).
            // await showTwitchPremiumNoticeModalThrottled(show, {source: 'twitch-vod-download'});

            twitchVodDetails = {
                isSubscriberOnly: true,
                url: premiumResult.value
            }
        }

        console.log({twitchVodDetails})

        finalVideo = {
            ...video,
            twitchVodDetails,
            videoFileName: `twitch-live-${id}`,
            videoName: "",
            videoPlaylist: "",
            videoThumbnail: "",
            fetchWithExtension: true,
            slug: null
        }
        console.log({twitchVodDetails, video})
    } else if (platform === "rumble") {
        let rumbleVod = {
            error: 'original'
        }

        const log = {
            platform, type, id, fromSec, channel
        };

        try {
            const isVideoId = url.includes('rumble.com/embed/');

            log.isVideoId = isVideoId;
            try {
                const response = await fetch(`/api/get-rumble?id=${encodeURIComponent(id)}&isVideoId=${isVideoId}`);
                log.getRumbleResponseStatus = response.status;
                log.getRumbleResponseOk = response.ok;
                if (!response.ok) {
                    const body = await response.json().catch(() => ({}))
                    throw {status: response.status, message: body.error || `Failed to fetch Rumble video`}
                }
                rumbleVod = await response.json()

                log.getRumbleResult = rumbleVod
            } catch (error) {
                log.getRumbleResultError = {...error};
                log.getRumbleResultErrorMessage = error?.message;
                throw {
                    status: 69,
                    msg: error?.status === 404 ? `Video doesn't exist`: `I couldn't fetch video details...`,
                }
            }

            console.log({rumbleVod})

            if (rumbleVod.source) {
                log.fetchType = 'source'
                // https://1a-1791.com/live/dn14c9xg/live-hls/4j0f-fnlb/chunklist_1080p_i1_DVR.m3u8
                // https://1a-1791.com/live/dn14c9xg/live-hls/4j0f-fnlb/chunklist_1080p_i0.m3u8

                let videoPlaylist;
                let type: ContentType;
                let response = null;

                const url = rumbleVod.source;
                let plText = '';
                try {
                    response = await fetch(url);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    plText = await response.text();
                    log.playlistFetchText = plText
                    const parsedPlaylist = hlsParse(plText);
                    // An empty body parses cleanly into a playlist with nothing in it, so
                    // check explicitly rather than treating that as a success.
                    if (!parsedPlaylist.segments?.length && !parsedPlaylist.variants?.length) {
                        throw new Error('empty playlist');
                    }
                    log.playlistFetchSuccess = plText;
                    videoPlaylist = url;
                } catch (err) {
                    log.playlistFetchError = err.message
                    log.playlistFetchStatus = response?.status

                    // if (err.message === 'NetworkError when attempting to fetch resource.') {
                    //     throw {
                    //         status: 69,
                    //         msg: 'Internet connection issues... 😬. Please, try again...'
                    //     }
                    // }
                    // if (err.message === 'Failed to fetch' || err.message === 'Load failed') {

                    let proxiedVideoPlaylist = url.replace('https://', window.location.origin + '/api/proxy/');
                    log.proxiedVideoPlaylist = proxiedVideoPlaylist
                    videoPlaylist = proxiedVideoPlaylist;

                    try {
                        response = await fetch(videoPlaylist);
                        if (!response.ok) throw new Error(`HTTP ${response.status}`);
                        plText = await response.text();
                        log.proxiedPlaylistFetchText = plText
                        const parsedPlaylist = hlsParse(plText);
                        if (!parsedPlaylist.segments?.length && !parsedPlaylist.variants?.length) {
                            throw new Error('empty playlist');
                        }
                        log.proxiedPlaylistFetchSuccess = plText;
                    } catch (err) {
                        log.proxiedPlaylistFetchError = err.message
                        log.proxiedPlaylistFetchStatus = response?.status
                        throw {
                            status: 69,
                            msg: `I couldn't fetch video playlist...`,
                        }
                    }
                }

                // const tempPlaylist = hlsParse(plText);
                // if(!tempPlaylist.variants.length) {
                //     throw new Error('no variants')
                // }
                // if(tempPlaylist.isMasterPlaylist) {
                //     try {
                //         response = await fetch(tempPlaylist.variants[0].uri);
                //         plText2 = await response.text()
                //     }catch(err) {
                //         track(`Stream fetch ERR #3`, {plText2, variant: tempPlaylist.variants[0].uri, status: response?.status, error: err.message});
                //     }
                //
                //     if(!plText2) {
                //         try {
                //             plText2 = await fetchText(`/api/proxy?url=${encodeURIComponent(tempPlaylist.variants[0].uri)}`);
                //         }catch(err) {
                //             track(`Stream fetch ERR #4`, {plText2, variant: tempPlaylist.variants[0].uri, error: err.message});
                //             throw new Error('cant fetch frist playlist QQ')
                //         }
                //     }
                //
                //     if(!plText2) throw new Error('cant fetch frist playlist')
                //     hlsParse(plText2);
                // }
                type = url.includes('/live') ? 'live' : 'vod';
                finalVideo = {
                    ...video,
                    type: type || 'vod',
                    videoPlaylist: videoPlaylist ? videoPlaylist : '',
                    downloadLink: videoPlaylist ? '' : rumbleVod.videoUrl,
                    audioUrl: rumbleVod.audioUrl,
                    duration: rumbleVod.duration,
                    videoFileName: slugify(`${rumbleVod.title || 'unknown'}`),
                    videoName: rumbleVod.title,
                    videoThumbnail: rumbleVod.thumbnail,
                    slug: null,
                }
                log.finalVideo = finalVideo
            } else if (rumbleVod.videoUrl) {
                log.fetchType = 'videoUrl'
                finalVideo = {
                    ...video,
                    type: rumbleVod.isShort ? 'short' : 'vod',
                    videoPlaylist: '',
                    downloadLink: rumbleVod.videoUrl,
                    audioUrl: rumbleVod.audioUrl,
                    duration: rumbleVod.duration,
                    videoFileName: slugify(rumbleVod.title || `rumble-video-${id}`),
                    videoName: rumbleVod.title,
                    videoThumbnail: rumbleVod.thumbnail,
                    slug: null
                }
                log.finalVideo = finalVideo
            } else {
                log.fetchType = 'undefined'
                throw {
                    status: 69,
                    msg: `I couldn't fetch video playlist...`,
                }
            }
        } catch (err) {
            log.uiError = err
            window.posthog?.logger.info('getVideoDetails.rumble:err', {...log})
            throw err
        }
        window.posthog?.logger.info('getVideoDetails.rumble:ok', {...log})
        console.log({finalVideo})
        // end rumble ========================================================v
    } else {

        throw new Error(`Unsupported platform/type: ${platform}/${type}`);
    }

    return finalVideo;
}
